import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Stack
} from '@mui/material';
import {
  AutoStories,
  Speed,
  BlockOutlined,
  AccessTime,
  ShieldMoon,
  Bolt
} from '@mui/icons-material';
import { createShopierPayment, getPackagePrice } from '../services/shopierService';

const PremiumPackage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadPrice();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus === 'failed') {
      alert('Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
    } else if (paymentStatus === 'error') {
      alert('Ödeme işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    }
  }, [location]);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        if (userData?.paymentStatus === 'completed' && 
            userData?.subscriptionType === 'premium' && 
            userData?.subscriptionEndDate?.toDate() > new Date()) {
          navigate('/dashboard');
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error('Abonelik kontrolü hatası:', error);
        setLoading(false);
      }
    };

    checkSubscription();
  }, [currentUser, navigate]);

  const loadPrice = async () => {
    try {
      const currentPrice = await getPackagePrice();
      setPrice(currentPrice);
    } catch (error) {
      console.error('Fiyat yüklenirken hata:', error);
    }
  };

  const handlePurchase = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      await createShopierPayment(currentUser);
    } catch (error) {
      console.error('Ödeme başlatılırken hata:', error);
      alert('Ödeme başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#00bf0d' }} />
      </Box>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: { xs: 2, sm: 4, md: 6 },
        px: { xs: 2, sm: 3 }
      }}
    >
      {/* Başlık */}
      <Stack spacing={1.5} sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 5 } }}>
        <Typography 
          variant={isMobile ? 'h4' : 'h3'} 
          sx={{ 
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' }
          }}
        >
          Premium Üyelik
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto',
            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
          }}
        >
          Sınırsız kelime öğrenme ve özel içeriklere erişim
        </Typography>
      </Stack>

      {/* Premium Kart */}
      <Paper 
        elevation={0}
        sx={{
          maxWidth: 800,
          mx: 'auto',
          border: '1px solid #eee',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
        }}
      >
        {/* Fiyat Başlığı */}
        <Box sx={{ 
          bgcolor: '#f8f9fa',
          p: { xs: 2.5, sm: 3, md: 4 },
          borderBottom: '1px solid #eee',
          textAlign: 'center'
        }}>
          <Typography 
            variant={isMobile ? 'h4' : 'h3'}
            sx={{ 
              fontWeight: 700,
              color: '#00bf0d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            <Bolt sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }} /> {price} TL
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#666',
              mt: 1,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
            }}
          >
            30 günlük tam erişim
          </Typography>
        </Box>

        {/* Özellikler */}
        <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Stack spacing={{ xs: 2.5, sm: 3 }}>
            <Feature
              icon={<AutoStories />}
              title="Sınırsız Kelime"
              description="Tüm kelime listelerine sınırsız erişim"
            />
            <Feature
              icon={<Speed />}
              title="Hızlı İlerleme"
              description="Kişiselleştirilmiş öğrenme deneyimi"
            />
            <Feature
              icon={<BlockOutlined />}
              title="Reklamsız"
              description="Kesintisiz öğrenme deneyimi"
            />
            <Feature
              icon={<AccessTime />}
              title="30 Gün Erişim"
              description="Tam kapsamlı premium üyelik"
            />
          </Stack>

          {/* Satın Al Butonu */}
          <Box sx={{ textAlign: 'center', mt: { xs: 3, sm: 4 } }}>
            <Button
              variant="contained"
              size="large"
              onClick={handlePurchase}
              disabled={loading}
              sx={{
                bgcolor: '#00bf0d',
                px: { xs: 4, sm: 6 },
                py: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#009f0b',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,191,13,0.2)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? 'İşleniyor...' : 'Hemen Başla'}
            </Button>

            {/* Güvenlik Bilgisi */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 3 }}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: { xs: 2, sm: 3 } }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: '#666',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                <ShieldMoon fontSize="small" />
                Güvenli Ödeme
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: '#666',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                <Bolt fontSize="small" />
                Anında Erişim
              </Box>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

// Özellik bileşeni
const Feature = ({ icon, title, description }) => (
  <Stack 
    direction="row" 
    spacing={{ xs: 1.5, sm: 2 }} 
    alignItems="flex-start"
  >
    <Box
      sx={{
        width: { xs: 36, sm: 40 },
        height: { xs: 36, sm: 40 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: { xs: 1.5, sm: 2 },
        bgcolor: '#f8f9fa',
        color: '#00bf0d'
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: { xs: 20, sm: 24 } } })}
    </Box>
    <Box>
      <Typography 
        variant="subtitle1"
        sx={{ 
          fontWeight: 600,
          color: '#1a1a1a',
          mb: 0.5,
          fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2"
        sx={{ 
          color: '#666',
          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
          lineHeight: 1.5
        }}
      >
        {description}
      </Typography>
    </Box>
  </Stack>
);

export default PremiumPackage; 